import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useMemo } from 'react';

import { inspectionMutations } from '@/entities/inspection';
import { useTenantDb } from '@/shared/offline';

import {
    readEnergyDraft,
    type EnergyDraft,
    type ShellEntry,
    type ShellPart,
} from '../model/energy';

const DRAFT_NAME = 'ep';

function readDraft(drafts: Record<string, unknown> | undefined): EnergyDraft {
    return readEnergyDraft(drafts?.[DRAFT_NAME]);
}

/**
 * De opname NTA 8800 schrijft lokaal, in de database van de actieve omgeving,
 * als concept op de opname (`inspection.drafts.ep`). Het draadformaat v1 kent
 * nog geen entiteit voor het energielabel; tot die er is gaat dit concept niet
 * over de lijn en staat er dus ook niets van in de outbox.
 */
export function useEnergyDraft(inspectionUuid: string) {
    const db = useTenantDb();

    const inspection = useLiveQuery(
        async () => (db ? await db.inspections.get(inspectionUuid) : undefined),
        [db, inspectionUuid],
    );
    const order = useLiveQuery(
        async () =>
            db && inspection?.orderUuid ? await db.orders.get(inspection.orderUuid) : undefined,
        [db, inspection?.orderUuid],
    );

    const draft = useMemo(() => readDraft(inspection?.drafts), [inspection]);

    /**
     * Schrijft een deel van het concept. Het bewaarde concept wordt gelezen in de
     * transactie die schrijft, zodat twee snelle wijzigingen elkaar niet
     * overschrijven.
     */
    const write = useCallback(
        async (change: (current: EnergyDraft) => EnergyDraft) => {
            if (!db) {
                return;
            }

            await inspectionMutations.updateDraft(
                inspectionUuid,
                DRAFT_NAME,
                (stored) => change(readEnergyDraft(stored)),
                db,
            );
        },
        [db, inspectionUuid],
    );

    const update = useCallback(
        (patch: Partial<Omit<EnergyDraft, 'schil'>>) =>
            write((current) => ({ ...current, ...patch })),
        [write],
    );

    const updateShell = useCallback(
        (part: ShellPart, patch: Partial<ShellEntry>) =>
            write((current) => ({
                ...current,
                schil: { ...current.schil, [part]: { ...current.schil[part], ...patch } },
            })),
        [write],
    );

    return { inspection, order, draft, update, updateShell };
}
